'use client';

import { SsoType, SsoTypeString } from '@diritto/common/account/constants/sso.constant';
import { TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { toMapByKey } from '@diritto/common/utils/array';
import { buildUrl } from '@diritto/common/utils/urls';
import { AccountApi } from 'api/ApiHelper';
import { SettingItem } from 'app-components/accounts/setting/SettingItem';
import { SettingSection } from 'app-components/accounts/setting/SettingSection';
import { useAuthState } from 'contexts/AuthProvider';
import { useTrackingState } from 'contexts/TrackingProvider';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import React, { useCallback, useMemo } from 'react';
import { useMarketingAgree } from 'state-hooks/accounts/useMarketingAgree';
import { BlockAuth } from 'ui-components/common/BlockAuth';
import { CertificateLink } from 'ui-components/common/CertificateLink';
import { SignLink } from 'ui-components/common/SignLink';
import { SwitchButton } from 'ui-components/common/SwitchButton';
import { ContentBodyContainer } from 'ui-components/layout/containers/ContentBodyCenterContainer';
import { MutableHeaderLayout } from 'ui-components/layout/MutableHeaderLayout';
import { useRequestSsoLink } from 'ui-hooks/useRequestSsoLink';

import { showAlertModal, showToast } from 'utils/modal';
import { SentryService } from 'utils/SentryService';
import { buildCertificateUrl } from 'utils/url';

export const DirittoAccountPage = () => {
  const { tracker } = useTrackingState();
  const { isLoggedIn, user, ssoList, refetch, isLoaded, partner } = useAuthState();
  const router = useRouter();
  const pathname = usePathname();
  const requestSsoLink = useRequestSsoLink();
  const { marketingAgree, handleChangeMarketingAgree, handleChangeNightMarketingAgree } = useMarketingAgree();
  const ssoMap = useMemo(() => toMapByKey('ssoType', ssoList ?? []), [ssoList]);

  const handleSsoClick = useCallback(
    (ssoType: SsoType) => {
      tracker.track(TrackingEventType.LinkSsoAtSign);

      const loginAbleSsoCount =
        ssoList?.filter(sso => sso.ssoType !== SsoType.Twitter && sso.ssoType !== SsoType.Ridi).length ?? 0;

      if (ssoMap[ssoType]) {
        if (loginAbleSsoCount > 1) {
          showAlertModal(
            {
              title: `${SsoTypeString[ssoType]} SNS 계정 연결 해제`,
              description: `연결을 해제하면 더 이상 ${SsoTypeString[ssoType]} SNS 계정으로 로그인할 수 없어요.`,
            },
            {
              cancelText: '취소',
              confirmText: '해제하기',
              onConfirm: async () => {
                try {
                  await AccountApi.unlinkSso(ssoType);
                  refetch().then();
                  showToast('SNS 계정 연결이 해제되었어요.');
                } catch (e) {
                  SentryService.captureException(e);
                  showToast(
                    '정상적으로 처리되지 않았어요. 다시 시도해주세요. 문제가 지속되면 고객센터에 문의해주세요.',
                    { extraShowTimeSec: 5 },
                  );
                }
              },
            },
          );
        } else {
          showToast('연결된 SNS 계정이 최소 1개 이상이어야 해요.');
        }
      } else if (ssoType === SsoType.Twitter) {
        showToast('지원이 종료된 SNS예요.');
      } else {
        const url = buildUrl('/accounts/link', {
          ssoType,
          returnUrl: '/accounts/diritto-account',
        });
        router.push(url);
      }
    },
    [refetch, router, ssoList, ssoMap, tracker],
  );

  const handleRidiSsoClick = useCallback(() => {
    tracker.track(TrackingEventType.LinkSsoAtSign);
    const ssoType = SsoType.Ridi;

    if (ssoMap[ssoType]) {
      showAlertModal(
        {
          title: '리디 계정 연결을 해제하시겠어요?',
          description: '연결 해제 후, 동일 리디 계정을 다시 디리토 계정에 연결하려면 30일 이후에 가능해요.',
        },
        {
          cancelText: '취소',
          confirmText: '리디 계정 연결 해제하기',
          onConfirm: async () => {
            try {
              await AccountApi.unlinkSso(ssoType);
              refetch().then();
              showToast('리디 계정 연결이 해제되었어요.');
            } catch (e) {
              SentryService.captureException(e);
              showToast('정상적으로 처리되지 않았어요. 다시 시도해주세요. 문제가 지속되면 고객센터에 문의해주세요.', {
                extraShowTimeSec: 5,
              });
            }
          },
        },
      );
    } else {
      showAlertModal(
        {
          title: '리디 계정을 연결하시겠어요?',
          description: '연결 후, 동일 리디 계정을 다시 디리토 계정에 연결하려면 30일 이후에 가능해요.',
        },
        {
          cancelText: '취소',
          confirmText: '리디 계정 연결하기',
          onConfirm: () => requestSsoLink(ssoType),
        },
      );
    }
  }, [refetch, requestSsoLink, ssoMap, tracker]);

  return (
    <MutableHeaderLayout title="디리토 계정">
      {isLoaded && !isLoggedIn && <BlockAuth message="로그인 후\n디리토를 이용해 보세요!" />}
      {isLoggedIn && (
        <ContentBodyContainer className="flex flex-col gap-[32px]">
          <SettingSection title="알림">
            <SettingItem title="이벤트 / 추천 알림" description="추천 작품이나 이벤트 등을 알려드려요.">
              <SwitchButton
                id="marketing-agreement"
                checked={marketingAgree.agree}
                onChange={handleChangeMarketingAgree}
              />
            </SettingItem>

            <SettingItem
              title="야간 이벤트 / 추천 알림"
              description="21시~8시 사이에 이벤트 / 추천 작품을 알려 드려요.">
              <SwitchButton
                id="marketing-agreement-night"
                checked={marketingAgree.agreeNight}
                onChange={handleChangeNightMarketingAgree}
              />
            </SettingItem>
          </SettingSection>

          {!partner && (
            <SettingSection title="연결된 SNS 계정 관리">
              <SettingItem
                title={`⚠️ ${SsoTypeString[SsoType.Twitter]}`}
                description="트위터 로그인은 종료되어 사용할 수 없어요.">
                <SwitchButton id={`sso-link-${SsoType.Twitter}`} checked={false} disabled />
              </SettingItem>
              <SettingItem title={SsoTypeString[SsoType.Kakao]} description={ssoMap[SsoType.Kakao]?.ssoEmail}>
                <SwitchButton
                  id={`sso-link-${SsoType.Kakao}`}
                  checked={!!ssoMap[SsoType.Kakao]}
                  onChange={() => handleSsoClick(SsoType.Kakao)}
                />
              </SettingItem>
              <SettingItem title={SsoTypeString[SsoType.Naver]} description={ssoMap[SsoType.Naver]?.ssoEmail}>
                <SwitchButton
                  id={`sso-link-${SsoType.Naver}`}
                  checked={!!ssoMap[SsoType.Naver]}
                  onChange={() => handleSsoClick(SsoType.Naver)}
                />
              </SettingItem>
              <SettingItem title="애플" description={ssoMap[SsoType.Apple]?.ssoEmail}>
                <SwitchButton
                  id={`sso-link-${SsoType.Apple}`}
                  checked={!!ssoMap[SsoType.Apple]}
                  onChange={() => handleSsoClick(SsoType.Apple)}
                />
              </SettingItem>

              <SettingItem title={SsoTypeString[SsoType.Google]} description={ssoMap[SsoType.Google]?.ssoEmail}>
                <SwitchButton
                  id={`sso-link-${SsoType.Google}`}
                  checked={!!ssoMap[SsoType.Google]}
                  onChange={() => handleSsoClick(SsoType.Google)}
                />
              </SettingItem>
            </SettingSection>
          )}

          <SettingSection title="내 디리토 계정">
            {!partner && (
              <SettingItem title="리디 계정 연결" description={ssoMap[SsoType.Ridi]?.ssoEmail}>
                <SwitchButton
                  id={`sso-link-${SsoType.Ridi}`}
                  checked={!!ssoMap[SsoType.Ridi]}
                  onChange={handleRidiSsoClick}
                />
              </SettingItem>
            )}

            {user?.ageVerified && !partner && (
              <SettingItem title="본인인증">
                <p className="text-xs line-clamp-1 text-gray-500">완료</p>
              </SettingItem>
            )}
            {!user?.ageVerified && (
              <CertificateLink href={buildCertificateUrl(pathname ?? '')}>
                <SettingItem title="본인인증" />
              </CertificateLink>
            )}
            {partner?.verifiedAt ? (
              <Link href="https://dirittohelp.zendesk.com" target="_blank">
                <SettingItem title="탈퇴하기" description="파트너 계정 탈퇴 시 고객센터에 문의해주세요." />
              </Link>
            ) : (
              <SignLink href="/accounts/withdraw">
                <SettingItem title="탈퇴하기" />
              </SignLink>
            )}
          </SettingSection>
        </ContentBodyContainer>
      )}
    </MutableHeaderLayout>
  );
};
